'use client';

import {
  useEffect,
  useId,
  useRef,
  type FunctionComponent,
  type KeyboardEvent,
} from 'react';
import { useScopedI18n } from '@locales/client';

import type { GamingLimitHardRejectionPayload } from '../../model/gaming-limits';
import {
  HardModalActions,
  HardModalBackdrop,
  HardModalButton,
  HardModalButtonText,
  HardModalDialog,
  HardModalIllustration,
  HardModalMain,
  HardModalMessage,
  HardModalTitle,
} from './gaming-limits-hard-modal.styles';

interface GamingLimitsHardModalProps {
  rejection: GamingLimitHardRejectionPayload;
  onFinishGame: () => void;
  onChangeLimit: () => void;
}

export const GamingLimitsHardModal: FunctionComponent<
  GamingLimitsHardModalProps
> = ({ rejection, onFinishGame, onChangeLimit }) => {
  const t = useScopedI18n('profile.account.gaming_limits.hard_modal');
  const titleId = useId();
  const descriptionId = useId();
  const finishButtonRef = useRef<HTMLButtonElement>(null);
  const changeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    finishButtonRef.current?.focus();

    return () => {
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, []);

  const trapFocus = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab') return;

    const first = finishButtonRef.current;
    const last = changeButtonRef.current;
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <HardModalBackdrop>
      <HardModalDialog
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onKeyDown={trapFocus}
      >
        <HardModalMain>
          <HardModalTitle id={titleId}>{t('title')}</HardModalTitle>
          <HardModalIllustration
            src="/images/gaming-limits/hard-limit-door.png"
            alt=""
            aria-hidden="true"
          />
          <HardModalMessage id={descriptionId}>
            {t('message', {
              period: t(`periods.${rejection.period}`),
            })}
          </HardModalMessage>
        </HardModalMain>

        <HardModalActions>
          <HardModalButton
            ref={finishButtonRef}
            type="button"
            $variant="danger"
            onClick={onFinishGame}
          >
            <HardModalButtonText>{t('finish_game')}</HardModalButtonText>
          </HardModalButton>
          <HardModalButton
            ref={changeButtonRef}
            type="button"
            $variant="secondary"
            onClick={onChangeLimit}
          >
            <HardModalButtonText>{t('change_limit')}</HardModalButtonText>
          </HardModalButton>
        </HardModalActions>
      </HardModalDialog>
    </HardModalBackdrop>
  );
};
